/* Gift With Purchase (GWP) module
 * - Tracks utm_discount param and stores code in sessionStorage
 * - Auto applies/removes discount code based on UTM code availability
 * - UTM code matches the discount code for cart application
 * - Refreshes page after successful discount application/removal
 */

(function() {
  const STORAGE_KEY = 'gwp_code';
  const DISCOUNT_STATE_KEY = 'gwp_discount_state';
  const URL_PARAM = 'utm_discount';
  let isSyncing = false;

  const getSetting = (id, fallback) => {
    try {
      // Expose settings via theme liquid onto window.themeSettings if needed in future
      if (window && window.Shopify && Shopify.theme && Shopify.theme.settings && (id in Shopify.theme.settings)) {
        return Shopify.theme.settings[id];
      }
    } catch(e) {}
    try {
      // Fallback: parse inline settings element if present
      const el = document.getElementById('theme-settings-json');
      if (el) {
        const json = JSON.parse(el.textContent || '{}');
        if (id in json) return json[id];
      }
    } catch(e) {}
    return typeof fallback !== 'undefined' ? fallback : undefined;
  };

  const readQueryParam = (name) => {
    const params = new URLSearchParams(window.location.search);
    return params.get(name);
  };

  const getStoredGwpCode = () => {
    try { return sessionStorage.getItem(STORAGE_KEY); } catch(e) { return null; }
  };

  const setStoredGwpCode = (code) => {
    try {
      if (code) sessionStorage.setItem(STORAGE_KEY, code);
    } catch(e) {}
  };

  const getDiscountState = () => {
    try { 
      const state = sessionStorage.getItem(DISCOUNT_STATE_KEY);
      return state ? JSON.parse(state) : null;
    } catch(e) { 
      return null; 
    }
  };

  const setDiscountState = (state) => {
    try {
      sessionStorage.setItem(DISCOUNT_STATE_KEY, JSON.stringify(state));
    } catch(e) {}
  };

  // Function to validate discount application and refresh page if successful
  const applyDiscountAndRefresh = async (discountCode) => {
    try {
      const response = await fetch(window.Shopify.routes.root + 'cart/update.js', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          discount: discountCode
        })
      });

      if (response.ok) {
        const cartData = await response.json();
        console.log('Cart update response:', cartData);
        
        // Check if discount was successfully applied
        const wasDiscountApplied = validateDiscountApplication(cartData, discountCode);
        
        if (wasDiscountApplied) {
          console.log(`Discount "${discountCode}" applied successfully!`);
          
          // Only refresh if this is a new application
          const currentState = getDiscountState();
          const isNewApplication = !currentState || !currentState.applied || currentState.code !== discountCode;
          
          // Update state to prevent future unnecessary applications
          setDiscountState({ applied: true, code: discountCode });
          
          if (isNewApplication) {
            console.log('Refreshing page...');
            setTimeout(() => {
              window.location.reload();
            }, 500);
          }
          
          return true;
        } else {
          console.log(`Discount "${discountCode}" was not applied or is not valid`);
          return false;
        }
      } else {
        console.error('Failed to update cart:', response.status);
        return false;
      }

    } catch (error) {
      console.error('Error applying discount:', error);
      return false;
    }
  };

  // Function to remove discount and refresh page if successful
  const removeDiscountAndRefresh = async () => {
    try {
      const response = await fetch(window.Shopify.routes.root + 'cart/update.js', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          discount: ''
        })
      });

      if (response.ok) {
        const cartData = await response.json();
        console.log('Cart update response (discount removal):', cartData);
        
        // Check if discount was successfully removed
        const wasDiscountRemoved = !cartData.discount_codes || cartData.discount_codes.length === 0;
        
        if (wasDiscountRemoved) {
          console.log('Discount removed successfully!');
          
          // Update state to prevent future unnecessary removals
          setDiscountState({ applied: false, code: null });
          
          // Only refresh if there was a discount applied before
          const currentState = getDiscountState();
          if (currentState && currentState.applied) {
            console.log('Refreshing page...');
            setTimeout(() => {
              window.location.reload();
            }, 500);
          }
          
          return true;
        } else {
          console.log('Discount was not removed');
          return false;
        }
      } else {
        console.error('Failed to update cart:', response.status);
        return false;
      }

    } catch (error) {
      console.error('Error removing discount:', error);
      return false;
    }
  };

  // Validation function to check if discount was successfully applied
  const validateDiscountApplication = (cartData, expectedDiscountCode) => {
    // Check if discount_codes array exists and has entries
    if (!cartData.discount_codes || cartData.discount_codes.length === 0) {
      console.log('No discount codes found in cart');
      return false;
    }
    
    // Look for the specific discount code we applied
    const appliedDiscount = cartData.discount_codes.find(discount => 
      discount.code === expectedDiscountCode
    );
    
    if (!appliedDiscount) {
      console.log(`Discount code "${expectedDiscountCode}" not found in applied discounts`);
      return false;
    }
    
    // Check if the discount is applicable (valid)
    if (appliedDiscount.applicable !== true) {
      console.log(`Discount code "${expectedDiscountCode}" found but not applicable`);
      console.log('Discount details:', appliedDiscount);
      return false;
    }
    
    console.log(`Discount code "${expectedDiscountCode}" successfully applied and applicable`);
    return true;
  };

  const syncDiscountWithCart = async () => {
    if (isSyncing) return;
    
    const enabled = getSetting('gwp_enabled', false);
    if (!enabled) return;

    const storedCode = getStoredGwpCode();
    const currentState = getDiscountState();
    
    // Check if we need to make any changes
    const shouldApplyDiscount = storedCode && (!currentState || !currentState.applied || currentState.code !== storedCode);
    const shouldRemoveDiscount = !storedCode && currentState && currentState.applied;
    
    if (!shouldApplyDiscount && !shouldRemoveDiscount) {
      console.log('GWP: No discount state change needed');
      return;
    }
    
    isSyncing = true;
    
    try {
      if (shouldApplyDiscount) {
        // UTM code is available, apply discount
        console.log(`GWP: Applying discount code: ${storedCode}`);
        await applyDiscountAndRefresh(storedCode);
      } else if (shouldRemoveDiscount) {
        // UTM code is unavailable, remove discount
        console.log('GWP: No UTM code found, removing discount');
        await removeDiscountAndRefresh();
      }
    } catch (error) {
      console.error('GWP: Error syncing discount:', error);
    } finally {
      isSyncing = false;
    }
  };

  const initFromUrl = () => {
    const code = readQueryParam(URL_PARAM);
    if (code) setStoredGwpCode(code);
  };

  const subscribeCartUpdates = () => {
    try {
      if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
        subscribe(PUB_SUB_EVENTS.cartUpdate, (event) => {
          // Prevent infinite loops by ignoring events from GWP script
          if (event && event.source === 'gwp') {
            return;
          }
          
          // Sync the discount logic
          syncDiscountWithCart();
        });
      }
    } catch(e) {
      console.error('GWP: Error subscribing to cart updates:', e);
    }
  };

  document.addEventListener('DOMContentLoaded', function() {
    initFromUrl();
    subscribeCartUpdates();
    // Initial sync on page load
    syncDiscountWithCart();
  });
})();


